import { gql } from '@apollo/client'
import {
  BodyText,
  Caption,
  ChipClickable,
  EditIcon,
  Heading,
  List,
  ListElement,
  Separator,
  Subtitle,
} from '@farol-ds/react'
import { ARTIFACT_LABEL, printStyles } from '@jusbrasil-web/docview/core'
import { nullableConnectionNodes } from '@jusbrasil-web/shared-apollo-client/utilities'
import { LoginTrigger } from '@jusbrasil-web/shared-bifrost'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  COMPONENT_NAME,
  GA4ClickEvent,
  GA4CustomComponentRenderedEvent,
  SELECTED_OPTION,
} from '@jusbrasil-web/shared-ga4-events'
import { LineScroll, useLineScroll } from '@jusbrasil-web/shared-line-scroll'
import classNames from 'classnames'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { useSegmentation } from '../../hooks/segmentation-hook'
import { AIDisclaimer } from '../ai-disclaimer'
import { EditDocumentTopics } from '../edit-document-topics'
import { NewsDisclaimer } from '../news-disclaimer'
import {
  PublicationHeader_DocumentFragment,
  PublicationHeader_ViewerFragment,
} from './__generated__/publication-header.graphql'
import styles from './publication-header.module.scss'

export interface HeaderProps {
  document: PublicationHeader_DocumentFragment
  viewer: PublicationHeader_ViewerFragment | null
  toolbar: ReactNode
  hideToolbar?: boolean
}

type Topic = {
  tid: number
  title: string
  url: string
}

export function PublicationHeader(props: HeaderProps) {
  const { document, viewer, toolbar, hideToolbar = false } = props
  const { title, subtitle, topics, publisher, artifact } = document
  const publisherIsViewer = publisher?.isViewer

  const segmentationData = useSegmentation()

  const lineScrollListRef = useRef<ListElement>(null)
  const {
    boundaryRef: lineScrollBoundaryRef,
    arrowLeftRef: lineScrollArrowLeftRef,
    arrowRightRef: lineScrollArrowRightRef,
    scrollToDirection,
    captureTargetRef,
  } = useLineScroll({ listRef: lineScrollListRef, scrollStrategy: 'visible-width' })

  const topicList = nullableConnectionNodes(topics)
  const [updatedTopicList, setUpdatedTopicList] = useState<Topic[]>(
    topicList.map((topic) => ({ tid: topic.tid, title: topic.title || '', url: topic.url || '#' }))
  )
  const [isOpen, setIsOpen] = useState(false)
  const { sendGA4Event } = useGA4Dispatcher()

  useEffect(() => {
    if (updatedTopicList.length > 0) {
      sendGA4Event(
        new GA4CustomComponentRenderedEvent({
          componentName: COMPONENT_NAME.RELATED_MODEL_TOPIC,
          componentClass: COMPONENT_CLASS.RELATED_TOPIC_POPOVER,
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTopicList])

  const onTopicChipClick = () => {
    sendGA4Event(
      new GA4ClickEvent({
        componentName: COMPONENT_NAME.RELATED_MODEL_TOPIC,
        componentClass: COMPONENT_CLASS.RELATED_TOPIC_POPOVER,
        selectedOption: SELECTED_OPTION.RELATED_TOPIC_SEARCH,
      })
    )
  }
  const modelArticleArtifacts = [ARTIFACT_LABEL.MODELO_PECA, ARTIFACT_LABEL.ARTIGO] as const

  return (
    <div className={styles.header}>
      <EditDocumentTopics
        isOpen={isOpen}
        document={document}
        topics={updatedTopicList}
        onAddTopic={(topic) => setUpdatedTopicList([...updatedTopicList, topic])}
        onRemoveTopic={(topic) =>
          setUpdatedTopicList(updatedTopicList.filter((t) => t.tid !== topic.tid))
        }
        onOpenChange={(open) => {
          setIsOpen(open)
        }}
      />

      {!segmentationData && (
        <>
          {publisherIsViewer && updatedTopicList.length === 0 ? (
            <BodyText size="md" className={printStyles.unprintable}>
              <strong>{publisher.name}</strong>, adicione tópicos e garanta que seu documento
              chegará à comunidade.{' '}
              <ChipClickable
                size="md"
                className={styles.chipContent}
                onClick={() => {
                  setIsOpen(true)
                }}
              >
                Adicionar
              </ChipClickable>
            </BodyText>
          ) : (
            <LineScroll className={printStyles.unprintable}>
              <LineScroll.ArrowLeft
                ref={lineScrollArrowLeftRef}
                scrollToDirection={scrollToDirection}
              />
              <LineScroll.Boundary
                ref={lineScrollBoundaryRef}
                containerClassName={styles.lineScrollContainer}
              >
                <List className={styles.chipsList} ref={lineScrollListRef}>
                  {updatedTopicList.map((topic) => (
                    <List.Item key={topic.title}>
                      <ChipClickable
                        ref={captureTargetRef}
                        size="md"
                        className={styles.chip}
                        title={topic.title}
                        unstable_truncate
                        asChild
                      >
                        <a
                          href={topic.url}
                          onClick={onTopicChipClick}
                          className={`document-topic-popover-trigger ${styles.chipContent}`}
                          rel={`${topic.tid}`}
                        >
                          {topic.title}
                        </a>
                      </ChipClickable>
                    </List.Item>
                  ))}
                  <List.Item>
                    <LoginTrigger
                      isLogged={!!viewer}
                      options={{
                        eventData: {
                          from_component: 'docview/EditDocumentTopics',
                        },
                      }}
                    >
                      <ChipClickable
                        size="md"
                        leftIcon={<EditIcon />}
                        className={styles.chipContent}
                        onClick={(e) => {
                          if (!viewer) {
                            e.preventDefault()
                            return
                          }
                          setIsOpen(true)
                        }}
                      >
                        Adicione tópicos
                      </ChipClickable>
                    </LoginTrigger>
                  </List.Item>
                </List>
              </LineScroll.Boundary>
              <LineScroll.ArrowRight
                ref={lineScrollArrowRightRef}
                scrollToDirection={scrollToDirection}
              />
            </LineScroll>
          )}
        </>
      )}

      {[ARTIFACT_LABEL.MODELO_PECA, ARTIFACT_LABEL.ARTIGO].includes(
        artifact as (typeof modelArticleArtifacts)[number]
      ) && <AIDisclaimer document={document} />}

      {artifact === ARTIFACT_LABEL.NOTICIAS && <NewsDisclaimer document={document} />}

      <div className={styles.titleWrapper}>
        <Heading
          className={classNames(styles.title, {
            [styles.segmentedArticleTitle]: !!segmentationData,
          })}
          asChild
        >
          <h1 dangerouslySetInnerHTML={{ __html: title || '' }} />
        </Heading>
        {subtitle && !!segmentationData && (
          <Caption className={styles.segmentedArticleSubtitle} asChild>
            <p dangerouslySetInnerHTML={{ __html: subtitle }} />
          </Caption>
        )}
        {subtitle && !segmentationData && (
          <Subtitle asChild>
            <p dangerouslySetInnerHTML={{ __html: subtitle }} />
          </Subtitle>
        )}
      </div>

      <div className={hideToolbar ? styles.toolbarWrapperHidden : styles.toolbarWrapper}>
        {toolbar}
      </div>

      <Separator className={styles.separator} />
    </div>
  )
}

PublicationHeader.fragments = {
  document: gql`
    fragment PublicationHeader_document on Document {
      artifact
      title
      subtitle
      topics(first: 1000, type: "MANUAL") {
        edges {
          node {
            tid
            title
            url
            tid
          }
        }
      }
      publisher {
        isViewer
        name
      }

      ...EditDocumentTopics_document
      ...AIDisclaimer_document
      ...NewsDisclaimer_document @include(if: $isNewsPage)
    }

    ${EditDocumentTopics.fragments.document}
    ${AIDisclaimer.fragments.document}
    ${NewsDisclaimer.fragments.document}
  `,

  viewer: gql`
    fragment PublicationHeader_viewer on Profile {
      pid
    }
  `,
}
